import { useState, useEffect, useMemo, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Scissors, User, CalendarDays, Clock, Check, ChevronRight, ChevronLeft,
  Lock, Phone, ArrowRight, Loader2, PartyPopper,
} from "lucide-react";
import {
  WEEKDAYS_SHORT, MONTHS, formatDateBR, maskPhone, toISODate, jsToPyWeekday,
} from "@/lib/format";
import { normalizeBrazilianWhatsAppPhone } from "@/lib/whatsappPhone";

const STEPS = ["Serviço", "Profissional", "Data", "Horário", "Seus dados", "Confirmação"];
const money = (cents) => (cents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function PublicBooking() {
  const { slug } = useParams();
  const [step, setStep] = useState(0);
  const [services, setServices] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [shopName, setShopName] = useState("Estabelecimento");
  const [shopLogo, setShopLogo] = useState(null);
  const [shopPhone, setShopPhone] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState({});
  const [paymentPolicy, setPaymentPolicy] = useState(null);

  const [service, setService] = useState(null);
  const [barber, setBarber] = useState(null);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [taxId, setTaxId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const bookingAttempt = useRef(null);
  const [confirmation, setConfirmation] = useState(null);
  const [tenantLoading, setTenantLoading] = useState(true);
  const [tenantError, setTenantError] = useState(false);
  const tenantParams = useMemo(() => (slug ? { tenant_slug: slug } : {}), [slug]);

  useEffect(() => {
    let active = true;
    setTenantLoading(true);
    setTenantError(false);
    api.get("/public/config", { params: tenantParams }).then(({ data }) => {
      if (!active) return;
      setShopName(data.name);
      setShopLogo(data.logo_url || null);
      setShopPhone(data.phone || null);
      setPaymentConfig(data);
      return api.get("/public/services", { params: tenantParams });
    }).then((response) => {
      if (active && response) setServices(response.data);
    }).catch(() => {
      if (active) setTenantError(true);
    }).finally(() => {
      if (active) setTenantLoading(false);
    });
    return () => { active = false; };
  }, [tenantParams]);

  // load barbers when service picked
  const pickService = async (s) => {
    setService(s);
    setPaymentPolicy(null);
    setBarber(null); setDate(null); setTime(null); setSchedule(null);
    try {
      const { data } = await api.get("/public/barbers", { params: { service_id: s.id, ...tenantParams } });
      setBarbers(data);
      if (data.length === 1) {
        await pickBarber(data[0], s);
        return;
      }
      setStep(1);
    } catch {
      toast.error("Erro ao carregar profissionais.");
    }
  };

  const pickBarber = async (b, s = service) => {
    setBarber(b);
    setPaymentPolicy(null);
    setDate(null); setTime(null);
    try {
      const [{ data }, policyResponse] = await Promise.all([
        api.get("/public/schedule", { params: { barber_id: b.id, ...tenantParams } }),
        api.get("/public/payment-policy", { params: { service_id: s.id, barber_id: b.id, ...tenantParams } }),
      ]);
      setSchedule(data);
      setPaymentPolicy(policyResponse.data);
      setStep(2);
    } catch {
      toast.error("Erro ao carregar agenda.");
    }
  };

  const availableDates = useMemo(() => {
    if (!schedule) return [];
    const out = [];
    const start = new Date();
    for (let i = 0; i < 45; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const iso = toISODate(d);
      const py = jsToPyWeekday(d.getDay());
      if (!schedule.working_weekdays.includes(py)) continue;
      if (schedule.closed_dates.includes(iso)) continue;
      out.push(d);
    }
    return out;
  }, [schedule]);

  const pickDate = async (d) => {
    const iso = toISODate(d);
    setDate(iso);
    setTime(null);
    setStep(3);
    setLoadingSlots(true);
    try {
      const { data } = await api.get("/public/availability", {
        params: { service_id: service.id, barber_id: barber.id, date: iso, ...tenantParams },
      });
      setSlots(data.slots);
    } catch {
      toast.error("Erro ao carregar horários.");
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const pickTime = (t) => {
    setTime(t);
    setStep(4);
  };

  const submit = async () => {
    if (name.trim().split(/\s+/).length < 2) return toast.error("Informe seu nome completo.");
    if (phone.replace(/\D/g, "").length < 10) return toast.error("Informe um WhatsApp válido com DDD.");
    setSubmitting(true);
    try {
      const body = {
        service_id: service.id, barber_id: barber.id, date, start: time,
        client_name: name.trim(), whatsapp: phone,
        ...(paymentPolicy?.mode !== "NONE" && paymentConfig.payment_provider === "PAGBANK" ? { email: email.trim(), tax_id: taxId.replace(/\D/g, "") } : {}),
      };
      const fingerprint = JSON.stringify([tenantParams, body]);
      if (bookingAttempt.current?.fingerprint !== fingerprint) {
        const key = Array.from(crypto.getRandomValues(new Uint8Array(16)), (value) => value.toString(16).padStart(2, "0")).join("");
        bookingAttempt.current = { fingerprint, key };
      }
      const { data } = await api.post("/public/appointments", body, {
        params: tenantParams, headers: { "Idempotency-Key": bookingAttempt.current.key },
      });
      setConfirmation(data);
      setStep(5);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = detail?.message || detail || "Não foi possível concluir o agendamento.";
      toast.error(msg);
      if (e?.response?.status === 409 && detail?.code !== "BOOKING_ATTEMPT_RECEIVED") {
        // horário tomado -> volta para escolher horário
        pickDate(new Date(date + "T12:00:00"));
        setStep(3);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    bookingAttempt.current = null;
    setStep(0); setService(null); setBarber(null); setDate(null);
    setTime(null); setName(""); setPhone(""); setEmail(""); setTaxId(""); setConfirmation(null);
  };

  const goBack = () => {
    if (step === 0) return;
    if (step === 1) return setStep(0);
    if (step === 2) return setStep(barbers.length > 1 ? 1 : 0);
    setStep(step - 1);
  };

  if (tenantLoading) {
    return <div className="min-h-screen flex items-center justify-center text-stone-400 font-sans2">Carregando estabelecimento…</div>;
  }

  if (tenantError) {
    return <div className="min-h-screen flex items-center justify-center px-4 text-center" data-testid="tenant-not-found">
      <div className="card-surface p-8 max-w-md">
        <Scissors className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
        <h1 className="font-display text-2xl gold-text">Estabelecimento não encontrado.</h1>
        <p className="mt-2 text-sm text-stone-400 font-sans2">Confira o link e tente novamente.</p>
      </div>
    </div>;
  }

  return (
    <div className="min-h-screen" data-theme={paymentConfig.theme_id || "PREMIUM_GOLD"}>
      {/* Header */}
      <header className="glass-header sticky top-0 z-40 bg-[#0A0B0E]/85 backdrop-blur-xl border-b border-[#282C37]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full flex items-center justify-center border border-[#D4AF37]/40 bg-[#D4AF37]/10 overflow-hidden">
              {shopLogo ? <img src={`${process.env.REACT_APP_BACKEND_URL || ""}${shopLogo}`} alt="" className="w-full h-full object-cover" /> : <Scissors className="w-5 h-5 text-[#D4AF37]" />}
            </div>
            <span className="font-display text-lg sm:text-xl gold-text">{shopName}</span>
          </div>
          <Link
            to="/admin/login"
            data-testid="nav-admin-link"
            className="flex items-center gap-1.5 text-xs sm:text-sm text-stone-400 hover:text-[#D4AF37] transition-colors font-sans2"
          >
            <Lock className="w-3.5 h-3.5" /> Admin
          </Link>
        </div>
      </header>

      {step === 5 ? (
        confirmation?.status === "PENDING_PAYMENT" ?
          <PaymentScreen initial={confirmation} shopPhone={shopPhone} onReset={reset} /> :
          <Confirmation data={confirmation} shopPhone={shopPhone} onReset={reset} />
      ) : (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 pb-28">
          {step === 0 && (
            <div className="mb-8 fade-in">
              <p className="badge-label font-mono2 text-xs uppercase tracking-widest text-[#D4AF37] mb-2">
                Agende em poucos toques
              </p>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-amber-50 leading-tight">
                Reserve seu horário
              </h1>
              <p className="font-sans2 text-stone-400 mt-2 text-sm sm:text-base">
                Sem cadastro. Escolha o serviço, o profissional e o melhor horário para você.
              </p>
            </div>
          )}

          <Stepper step={step} />

          {/* STEP 0: services */}
          {step === 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 fade-in">
              {services.map((s) => (
                <button
                  key={s.id}
                  data-testid={`service-card-${s.id}`}
                  onClick={() => pickService(s)}
                  className={`card-surface selectable text-left p-5 ${service?.id === s.id ? "selected-gold" : ""}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-sans2 font-semibold text-lg text-amber-50">{s.name}</h3>
                      {s.description && <p className="text-sm text-stone-400 mt-1">{s.description}</p>}
                    </div>
                    <ChevronRight className="w-5 h-5 text-[#D4AF37] shrink-0" />
                  </div>
                  <div className="flex items-center gap-3 mt-4 font-mono2 text-sm">
                    <span className="flex items-center gap-1 text-stone-300">
                      <Clock className="w-4 h-4 text-[#C57B57]" /> {s.duration} min
                    </span>
                    {s.price_fmt && <span className="text-[#D4AF37] font-semibold">{s.price_fmt}</span>}
                  </div>
                </button>
              ))}
              {services.length === 0 && (
                <p className="text-stone-500 font-sans2">Nenhum serviço disponível no momento.</p>
              )}
            </div>
          )}

          {/* STEP 1: barbers */}
          {step === 1 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 fade-in">
              {barbers.map((b) => (
                <button
                  key={b.id}
                  data-testid={`barber-card-${b.id}`}
                  onClick={() => pickBarber(b)}
                  className={`card-surface selectable p-5 flex items-center gap-4 ${barber?.id === b.id ? "selected-gold" : ""}`}
                >
                  <div data-testid={b.photo_url ? `barber-photo-${b.id}` : `barber-photo-fallback-${b.id}`} className="w-12 h-12 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center overflow-hidden">
                    {b.photo_url ? <img src={`${process.env.REACT_APP_BACKEND_URL || ""}${b.photo_url}`} alt={`Foto de ${b.name}`} className="w-full h-full object-cover" /> : <User className="w-6 h-6 text-[#D4AF37]" />}
                  </div>
                  <div className="text-left flex-1">
                    <h3 className="font-sans2 font-semibold text-amber-50">{b.name}</h3>
                    <p className="text-xs text-stone-400">Disponível para {service?.name}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[#D4AF37]" />
                </button>
              ))}
            </div>
          )}

          {/* STEP 2: date */}
          {step === 2 && (
            <div className="mt-6 fade-in">
              <h3 className="font-sans2 font-medium text-amber-100 mb-4 flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-[#D4AF37]" /> Escolha uma data disponível
              </h3>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2.5" data-testid="booking-date-picker">
                {availableDates.map((d) => {
                  const iso = toISODate(d);
                  return (
                    <button
                      key={iso}
                      data-testid={`date-${iso}`}
                      onClick={() => pickDate(d)}
                      className={`card-surface selectable py-3 text-center ${date === iso ? "selected-gold" : ""}`}
                    >
                      <div className="text-[10px] uppercase tracking-wide text-stone-400 font-mono2">
                        {WEEKDAYS_SHORT[jsToPyWeekday(d.getDay())]}
                      </div>
                      <div className="text-xl font-semibold text-amber-50 font-mono2">{d.getDate()}</div>
                      <div className="text-[10px] text-stone-500 font-mono2">{MONTHS[d.getMonth()]}</div>
                    </button>
                  );
                })}
              </div>
              {availableDates.length === 0 && (
                <p className="text-stone-500 font-sans2">Nenhuma data disponível para este profissional.</p>
              )}
            </div>
          )}

          {/* STEP 3: time */}
          {step === 3 && (
            <div className="mt-6 fade-in">
              <h3 className="font-sans2 font-medium text-amber-100 mb-1 flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#D4AF37]" /> Horários para {formatDateBR(date)}
              </h3>
              <p className="text-xs text-stone-500 mb-4 font-sans2">
                Duração do serviço: {service?.duration} min
              </p>
              {loadingSlots ? (
                <div className="flex items-center gap-2 text-stone-400 py-8 justify-center">
                  <Loader2 className="w-5 h-5 animate-spin" /> Carregando horários…
                </div>
              ) : slots.length === 0 ? (
                <div className="card-surface p-6 text-center text-stone-400 font-sans2">
                  Nenhum horário disponível neste dia. Tente outra data.
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2.5">
                  {slots.map((t) => (
                    <button
                      key={t}
                      data-testid={`time-slot-${t}`}
                      onClick={() => pickTime(t)}
                      className={`slot-btn py-3 font-mono2 font-medium ${time === t ? "slot-active" : "text-stone-200"}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* STEP 4: details */}
          {step === 4 && (
            <div className="mt-6 fade-in max-w-md">
              <div className="card-surface p-5 mb-6 space-y-2">
                <SummaryRow label="Serviço" value={service?.name} />
                <SummaryRow label="Profissional" value={barber?.name} />
                <SummaryRow label="Data" value={formatDateBR(date)} />
                <SummaryRow label="Horário" value={time} />
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-sans2 text-stone-300 mb-1.5 block">Nome completo</label>
                  <input
                    data-testid="client-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ex.: João da Silva"
                    className="w-full bg-[#12141A] border border-[#282C37] rounded-lg px-4 py-3 text-amber-50 placeholder-stone-600 focus:border-[#D4AF37] focus:outline-none font-sans2"
                  />
                </div>
                <div>
                  <label className="text-sm font-sans2 text-stone-300 mb-1.5 block flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5" /> WhatsApp (com DDD)
                  </label>
                  <input
                    data-testid="client-whatsapp-input"
                    value={phone}
                    onChange={(e) => setPhone(maskPhone(e.target.value))}
                    placeholder="(11) 91234-5678"
                    inputMode="numeric"
                    className="w-full bg-[#12141A] border border-[#282C37] rounded-lg px-4 py-3 text-amber-50 placeholder-stone-600 focus:border-[#D4AF37] focus:outline-none font-mono2"
                  />
                </div>
                {paymentPolicy?.mode !== "NONE" && paymentConfig.payment_provider === "PAGBANK" && <>
                  <div><label className="text-sm font-sans2 text-stone-300 mb-1.5 block">E-mail para pagamento</label>
                    <input data-testid="client-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-[#12141A] border border-[#282C37] rounded-lg px-4 py-3 text-amber-50" /></div>
                  <div><label className="text-sm font-sans2 text-stone-300 mb-1.5 block">CPF ou CNPJ para pagamento</label>
                    <input data-testid="client-tax-id-input" inputMode="numeric" value={taxId} onChange={(e) => setTaxId(e.target.value)} className="w-full bg-[#12141A] border border-[#282C37] rounded-lg px-4 py-3 text-amber-50" /></div>
                </>}
                {paymentPolicy && <div className="text-sm text-stone-300 space-y-1"><p>Valor do serviço: {money(paymentPolicy.price_cents)}</p><p>{paymentPolicy.mode === "FULL_PREPAY" ? "Pagamento integral agora" : paymentPolicy.mode === "DEPOSIT" ? "Sinal agora" : "Necessário agora"}: {money(paymentPolicy.amount_cents)}</p><p>Saldo restante: {money(paymentPolicy.balance_cents)}</p>{paymentPolicy.mode === "FULL_PREPAY" && <p>Pagamento integral necessário para confirmar este agendamento.</p>}</div>}
                {paymentPolicy?.mode !== "NONE" && paymentPolicy?.payment_available === false && <p className="text-sm text-amber-400">Pagamento temporariamente indisponível. Tente mais tarde.</p>}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Fixed bottom nav */}
      {step < 5 && (
        <div className="fixed bottom-0 left-0 right-0 bg-[#0A0B0E]/90 backdrop-blur-xl border-t border-[#282C37] z-40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
            <button
              data-testid="wizard-back-btn"
              onClick={goBack}
              disabled={step === 0}
              className="flex items-center gap-1.5 text-stone-400 hover:text-amber-100 disabled:opacity-30 font-sans2 text-sm transition-colors"
            >
              <ChevronLeft className="w-4 h-4" /> Voltar
            </button>
            {step === 4 ? (
              <button
                data-testid="confirm-booking-submit-btn"
                onClick={submit}
                disabled={submitting || !paymentPolicy || (paymentPolicy.mode !== "NONE" && paymentPolicy.payment_available === false)}
                className="flex items-center gap-2 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0B0E] font-sans2 font-semibold px-6 py-3 rounded-lg transition-colors disabled:opacity-60"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {paymentPolicy?.mode === "FULL_PREPAY" ? "Reservar e pagar integralmente" : paymentPolicy?.mode === "DEPOSIT" ? "Reservar e pagar sinal" : "Confirmar agendamento"}
              </button>
            ) : (
              <span className="text-xs text-stone-500 font-mono2">
                Passo {step + 1} de {STEPS.length}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Stepper({ step }) {
  return (
    <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar">
      {STEPS.map((s, i) => (
        <div key={s} className="flex items-center gap-1.5 shrink-0">
          <div
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-sans2 transition-colors ${
              i === step
                ? "bg-[#D4AF37] text-[#0A0B0E] font-semibold"
                : i < step
                ? "text-[#D4AF37]"
                : "text-stone-600"
            }`}
          >
            {i < step ? <Check className="w-3 h-3" /> : <span className="font-mono2">{i + 1}</span>}
            <span className="hidden sm:inline">{s}</span>
          </div>
          {i < STEPS.length - 1 && <div className="w-3 h-px bg-[#282C37]" />}
        </div>
      ))}
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-stone-400 font-sans2">{label}</span>
      <span className="text-amber-50 font-medium font-sans2">{value}</span>
    </div>
  );
}

export function buildManualWhatsAppLink(phone, appointment, dateFmt, payment) {
  const normalizedPhone = normalizeBrazilianWhatsAppPhone(phone);
  if (!normalizedPhone || !appointment?.id) return null;

  const message = [
    "Reserva realizada ✅",
    "",
    `Nome: ${appointment.client_name || "Cliente"}`,
    `Data: ${dateFmt || appointment.date || ""}`,
    `Horário: ${appointment.start || ""}`,
    `Profissional: ${appointment.barber_name || ""}`,
    `Serviço: ${appointment.service_name || ""}`,
    ...(payment?.status === "Confirmado" && payment?.deposit_fmt ? [
      `${payment.payment_mode === "FULL_PREPAY" ? "Pagamento integral" : "Sinal pago"}: ${payment.deposit_fmt}`,
      `Saldo: ${payment.balance_fmt}`,
      `Tolerância de atraso: ${payment.late_tolerance_minutes} minutos`,
    ] : []),
    `Reserva: #${appointment.id}`,
  ].join("\n");
  return `https://wa.me/${normalizedPhone}?text=${encodeURIComponent(message)}`;
}

export function Confirmation({ data, shopPhone, onReset }) {
  const a = data?.appointment;
  const whatsappLink = buildManualWhatsAppLink(shopPhone, a, data?.date_fmt, data);
  return (
    <div className="max-w-md mx-auto px-4 py-12 fade-in" data-testid="booking-confirmation">
      <div className="card-surface p-8 text-center" style={{ background: "#12141A" }}>
        <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mb-5">
          <PartyPopper className="w-8 h-8 text-emerald-400" />
        </div>
        <h2 className="font-display text-2xl gold-text mb-1">Reserva confirmada!</h2>
        <p className="text-stone-400 font-sans2 text-sm mb-6">Seu horário foi reservado com sucesso.</p>

        <div className="space-y-2.5 text-left bg-[#0A0B0E]/50 rounded-xl p-5 border border-[#282C37]">
          <SummaryRow label="Serviço" value={a?.service_name} />
          <SummaryRow label="Profissional" value={a?.barber_name} />
          <SummaryRow label="Data" value={data?.date_fmt} />
          <SummaryRow label="Horário" value={a?.start} />
          <SummaryRow label="Duração prevista" value={`${a?.duration} min`} />
          {data?.price_fmt && <SummaryRow label="Valor" value={data.price_fmt} />}
          {data?.deposit_fmt && <SummaryRow label={data.payment_mode === "FULL_PREPAY" ? "Pagamento integral pago" : "Sinal pago"} value={data.deposit_fmt} />}
          {data?.balance_fmt && <SummaryRow label="Saldo estimado no atendimento" value={data.balance_fmt} />}
          {data?.deposit_fmt && <SummaryRow label="Tolerância de atraso" value={`${data.late_tolerance_minutes} minutos`} />}
          <SummaryRow label="Reserva" value={a?.id ? `#${a.id}` : "—"} />
        </div>

        {whatsappLink ? (
          <div className="mt-5">
            <p className="text-xs font-sans2 text-stone-500 mb-3">Envie a confirmação abaixo para o estabelecimento pelo WhatsApp.</p>
            <a
              data-testid="send-confirmation-whatsapp"
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-[#07110B] font-sans2 font-semibold py-3 rounded-lg transition-colors"
            >
              Enviar confirmação pelo WhatsApp
            </a>
          </div>
        ) : (
          <p data-testid="whatsapp-contact-unavailable" className="mt-5 text-xs font-sans2 text-stone-500">
            A confirmação está salva. O WhatsApp do estabelecimento ainda não está disponível.
          </p>
        )}

        <button
          data-testid="new-booking-btn"
          onClick={onReset}
          className="mt-7 w-full flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0B0E] font-sans2 font-semibold py-3 rounded-lg transition-colors"
        >
          Fazer novo agendamento <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export function PaymentScreen({ initial, shopPhone, onReset }) {
  const [data, setData] = useState(initial);
  const [now, setNow] = useState(Date.now());
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (data.status !== "PENDING_PAYMENT") return undefined;
    let active = true;
    const refresh = async () => {
      try {
        const response = await api.post(`/public/payments/${initial.payment_id}/status`, { access_token: initial.access_token });
        if (active) setData({ ...response.data, payment_id: initial.payment_id, access_token: initial.access_token });
      } catch { /* Keep showing the server's last known state and retry. */ }
    };
    const timer = setInterval(() => { setNow(Date.now()); refresh(); }, 5000);
    const clock = setInterval(() => setNow(Date.now()), 1000);
    return () => { active = false; clearInterval(timer); clearInterval(clock); };
  }, [data.status, initial.payment_id, initial.access_token]);
  if (data.status === "Confirmado") return <Confirmation data={data} shopPhone={shopPhone} onReset={onReset} />;
  if (data.status !== "PENDING_PAYMENT") return <div data-testid="payment-expired" className="max-w-md mx-auto px-4 py-12 text-center">
    <div className="card-surface p-8"><h2 className="font-display text-2xl gold-text">{data.status === "PAYMENT_REVIEW" ? "Pagamento em análise" : "Prazo encerrado"}</h2>
      <p className="mt-3 text-stone-400">{data.status === "PAYMENT_REVIEW" ? "O pagamento chegou após a liberação do horário. O estabelecimento precisa conferir sua reserva." : "O horário foi liberado. Escolha outro para tentar novamente."}</p>
      <button onClick={onReset} className="mt-6 bg-[#D4AF37] text-[#0A0B0E] px-6 py-3 rounded-lg font-semibold">Novo agendamento</button></div></div>;
  const seconds = Math.max(0, Math.ceil((new Date(data.expires_at).getTime() - now) / 1000));
  const countdown = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(data.pix_text); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Não foi possível copiar o código Pix."); }
  };
  return <div data-testid="payment-screen" className="max-w-md mx-auto px-4 py-8 pb-16">
    <div className="card-surface p-6 sm:p-8 text-center">
      <h2 className="font-display text-2xl gold-text">Reserva quase concluída</h2>
      <p className="mt-3 text-stone-400">Seu horário ficará reservado por:</p>
      <p data-testid="payment-countdown" className="font-mono2 text-3xl text-amber-50 mt-2">{countdown}</p>
      <p className="mt-4 text-[#D4AF37] font-semibold">{data.payment_mode === "FULL_PREPAY" ? "Pagamento integral" : "Sinal"}: {data.deposit_fmt}</p>
      <p className="text-sm text-stone-400 mt-1">Forma de pagamento: Pix</p>
      {data.qr_image && <img data-testid="payment-qr" src={data.qr_image} alt="QR Code Pix para pagar o sinal" className="mx-auto mt-5 w-48 h-48 bg-white p-2 rounded-lg" />}
      {data.pix_text && <><p className="mt-5 text-sm text-stone-300">Pix copia e cola</p>
        <div data-testid="payment-pix-text" className="mt-2 break-all rounded-lg bg-[#0A0B0E] p-3 text-xs text-stone-400 max-h-24 overflow-auto">{data.pix_text}</div>
        <button data-testid="copy-payment-pix" onClick={copy} className="mt-3 bg-[#D4AF37] text-[#0A0B0E] px-5 py-2 rounded-lg font-semibold">{copied ? "Copiado!" : "Copiar código Pix"}</button></>}
      <p className="mt-6 text-sm text-stone-400">O sinal será descontado do valor do serviço no atendimento.</p>
      <p className="mt-2 text-sm text-stone-400">Se o pagamento não for realizado até o fim do prazo, o horário será liberado.</p>
      {seconds === 0 && <p className="mt-3 text-xs text-stone-500">Conferindo o estado da reserva…</p>}
    </div>
  </div>;
}
